import { useState, useEffect, useRef, useCallback } from "react";
import IconStarFill from "@/components/icons/icon-star-fill";

export interface CarouselTestimonial {
	id: string;
	content: string;
	authorName: string;
	authorHandle?: string;
	avatarUrl?: string;
	rating?: number;
}

export interface TestimonialCarouselProps {
	/** Array of testimonials to display */
	testimonials: CarouselTestimonial[];
	/** Background color of the carousel */
	backgroundColor?: string;
	/** Text color for the carousel content */
	textColor?: string;
	/** Whether to auto-advance slides */
	autoplay?: boolean;
	/** Seconds between slide transitions */
	autoplayInterval?: number;
	/** Whether to show navigation arrows */
	showNavigation?: boolean;
	/** Whether to show pagination dots */
	showPagination?: boolean;
	/** Whether to show the star rating */
	showRating?: boolean;
	/** Whether to show the author avatar */
	showAvatar?: boolean;
	/** Custom color for rating stars */
	ratingStarColor?: string;
	/** Optional className for additional styling */
	className?: string;
}

export default function TestimonialCarousel({
	testimonials,
	backgroundColor = "#7c3aed",
	textColor = "#ffffff",
	autoplay = false,
	autoplayInterval = 5,
	showNavigation = true,
	showPagination = true,
	showRating = true,
	showAvatar = true,
	ratingStarColor,
	className = "",
}: TestimonialCarouselProps) {
	const [currentIndex, setCurrentIndex] = useState(0);
	const autoplayTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

	const goToNext = useCallback(() => {
		setCurrentIndex((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1));
	}, [testimonials.length]);

	const goToPrevious = () => {
		setCurrentIndex((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1));
	};

	// Autoplay
	useEffect(() => {
		if (!autoplay || testimonials.length <= 1) return;

		autoplayTimerRef.current = setInterval(goToNext, autoplayInterval * 1000);

		return () => {
			if (autoplayTimerRef.current) {
				clearInterval(autoplayTimerRef.current);
			}
		};
	}, [autoplay, autoplayInterval, goToNext, testimonials.length]);

	if (testimonials.length === 0) return null;

	// Get initials for fallback avatar
	const getInitials = (name: string) => {
		return name
			.split(" ")
			.map((word) => word[0])
			.join("")
			.toUpperCase()
			.slice(0, 2);
	};

	return (
		<div className={`relative rounded-2xl px-8 py-12 md:px-12 md:py-16 ${className}`} style={{ backgroundColor, color: textColor }}>
			{/* Content */}
			<div className="flex items-center justify-center gap-6">
				{/* Left Arrow */}
				{showNavigation && (
					<button onClick={goToPrevious} className="flex-shrink-0 w-16 h-16 flex items-center justify-center opacity-70 hover:opacity-100 transition-opacity" style={{ color: textColor }} aria-label="Previous testimonial">
						<svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
						</svg>
					</button>
				)}

				{/* Testimonial Content - Grid overlay to maintain tallest height */}
				<div className="flex-1 min-w-0 text-center relative flex items-center">
					<div className="grid w-full">
						{testimonials.map((testimonial, index) => (
							<div key={testimonial.id} className={`col-start-1 row-start-1 transition-opacity duration-300 ${index === currentIndex ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
								{/* Rating */}
								{showRating && testimonial.rating && testimonial.rating > 0 && (
									<div className="flex justify-center gap-0.5 mb-4">
										{[1, 2, 3, 4, 5].map((star) => (
											<IconStarFill key={star} className={`w-5 h-5 ${star <= (testimonial.rating || 0) ? (ratingStarColor ? "" : "text-amber-400") : ""}`} style={star <= (testimonial.rating || 0) ? (ratingStarColor ? { color: ratingStarColor } : undefined) : { color: textColor, opacity: 0.3 }} />
										))}
									</div>
								)}

								<blockquote className="mb-6">
									<p className="text-xl md:text-xl font-medium leading-normal" style={{ color: textColor }}>{testimonial.content}</p>
								</blockquote>

								{/* Author Info */}
								<div className="flex items-center justify-center gap-3">
									{showAvatar &&
										(testimonial.avatarUrl ? (
											<img src={testimonial.avatarUrl} alt={testimonial.authorName} className="w-10 h-10 rounded-full object-cover border-2" style={{ borderColor: `color-mix(in srgb, ${textColor} 30%, transparent)` }} />
										) : (
											<div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium border-2" style={{ backgroundColor: `color-mix(in srgb, ${textColor} 20%, transparent)`, borderColor: `color-mix(in srgb, ${textColor} 30%, transparent)`, color: textColor }}>
												{getInitials(testimonial.authorName)}
											</div>
										))}
									<div>
										<p className="font-medium" style={{ color: textColor }}>
											{testimonial.authorName}
											{testimonial.authorHandle && <span className="font-normal" style={{ color: textColor, opacity: 0.7 }}>, {testimonial.authorHandle}</span>}
										</p>
									</div>
								</div>
							</div>
						))}
					</div>
				</div>

				{/* Right Arrow */}
				{showNavigation && (
					<button onClick={goToNext} className="flex-shrink-0 w-16 h-16 flex items-center justify-center opacity-70 hover:opacity-100 transition-opacity" style={{ color: textColor }} aria-label="Next testimonial">
						<svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
						</svg>
					</button>
				)}
			</div>

			{/* Dots Indicator */}
			{showPagination && (
				<div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex justify-center gap-2">
					{testimonials.map((_, index) => (
						<button
							key={index}
							onClick={() => setCurrentIndex(index)}
							className="w-2 h-2 rounded-full transition-all"
							style={index === currentIndex ? { backgroundColor: textColor, width: "1.5rem" } : { backgroundColor: textColor, opacity: 0.4 }}
							aria-label={`Go to testimonial ${index + 1}`}
						/>
					))}
				</div>
			)}
		</div>
	);
}
